/**
 * UserTagsTab — 全局用户标签定义（ADM-USR-S008）
 *
 * 1:1 对照 design/Novel-admin/src/pages/user-tags.jsx。
 * 无删除端点。§9.3 预置 tag_key 额外锁死删除与 key 编辑。
 * 新建强制未启用。未绑定不可启用。已打标用户数列契约未回，显示 —。
 *
 * 说明列长文本必须在单元格内截断：.tbl tbody td 是 white-space:nowrap，td 上的 maxWidth 在
 * table-layout:auto 下不生效，长 description 会把表格撑爆、后面几列叠在一起。故套内层固定宽
 * 容器 + ellipsis，全文走 title。
 */

import { type UserTagDto, isPresetUserTagKey } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useUserTagsPage } from '~/data/queries';
import { userTagsApi } from '~/hooks/use-user-tags';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { Section } from '~/ui/Section';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { UserTagEditor } from './dialogs/UserTagEditor';

function tri(t: UserTagDto): 'unbound' | 'bound_off' | 'bound_on' {
  if (t.binding_status === 'unbound') return 'unbound';
  return t.is_active ? 'bound_on' : 'bound_off';
}

const TRI_META = {
  unbound: {
    label: '未绑定',
    cls: 'chip-warn',
    desc: 'tag_key 未命中服务端分群规则注册表 · 不可启用',
  },
  bound_off: {
    label: '已绑定 · 已禁用',
    cls: '',
    desc: '规则已注册但当前禁用 · 自动打标停止，已有用户关联保留',
  },
  bound_on: {
    label: '已绑定 · 已启用',
    cls: 'chip-pos',
    desc: '有效启用 = 已绑定 ∧ is_active=true · 可进入人群筛选',
  },
} as const;

export default function UserTagsTab({ onFilterUsers }: { onFilterUsers: (id: string) => void }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 18;
  const [q, setQ] = useState('');
  const [fState, setFState] = useState<string[]>([]);
  const { data: all, isLoading, isError } = useUserTagsPage({ page: 1, pageSize: 200 });
  const [editor, setEditor] = useState<UserTagDto | 'new' | null>(null);
  const [toggleOf, setToggleOf] = useState<UserTagDto | null>(null);
  // 启停失败时 bump → Switch 重挂，把乐观翻过去的视觉态拨回真实值（仓内既有回滚模式）
  const [switchResetNonce, setSwitchResetNonce] = useState(0);

  const toggle = useMutation({
    mutationFn: (t: UserTagDto) => userTagsApi.toggle(t.id, { is_active: !t.is_active }),
    onSuccess: (row) => {
      toast(
        row.is_active
          ? `已启用「${row.name}」· 有效启用 = 已绑定 ∧ is_active=true`
          : `已停用「${row.name}」· 已有用户关联保留`,
        {
          tone: row.is_active ? 'pos' : 'warn',
          icon: row.is_active ? 'IconCheckCircle' : 'IconPause',
        },
      );
      void qc.invalidateQueries({ queryKey: ['user-tags'] });
      setToggleOf(null);
    },
    onError: (e) => {
      setSwitchResetNonce((n) => n + 1);
      toast(e instanceof AdminApiError ? e.message : '启停失败', { tone: 'neg' });
    },
  });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((t) => {
      if (
        needle &&
        !`${t.tag_key} ${t.name} ${t.description ?? ''}`.toLowerCase().includes(needle)
      ) {
        return false;
      }
      if (fState.length && !fState.includes(tri(t))) return false;
      return true;
    });
  }, [all, q, fState]);

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const usable = all.filter((t) => t.binding_status === 'bound' && t.is_active).length;

  const askToggle = (t: UserTagDto) => {
    if (!t.is_active && t.binding_status === 'unbound') {
      toast(
        `USER_TAG_RULE_UNBOUND · 「${t.name}」的 tag_key ${t.tag_key} 未命中服务端分群规则注册表，未绑定标签不可启用`,
        { tone: 'neg', icon: 'IconAlert' },
      );
      return;
    }
    setToggleOf(t);
  };

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div
        className="card"
        style={{ padding: '12px 14px', background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconInfo size={13} style={{ color: 'var(--info)', marginTop: 2 }} />
          <div style={{ fontSize: 11.5, lineHeight: 1.6 }}>
            标签定义<span style={{ fontWeight: 500 }}>全局一份</span>（无{' '}
            <span className="mono">app_id</span>，<span className="mono">tag_key</span>{' '}
            全局唯一，落行即对所有 App 生效）。
            <span className="mono">binding_status</span> 由服务端分群规则注册表按 tag_key 派生，
            <span style={{ fontWeight: 500 }}>不可录入</span>；App 维度只存在于{' '}
            <span className="mono">user_tag_assignments</span> 关联行，admin 无该表写端点。
            <span style={{ color: 'var(--fg-muted)' }}>
              {' '}
              无删除端点：停止使用某标签用 <span className="mono">is_active=false</span> 表达。§9.3
              预置标签不可删除、不可改 <span className="mono">tag_key</span>。
            </span>
          </div>
        </div>
      </div>

      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <SearchInput
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="搜索 tag_key / 名称 / 说明…"
          width={260}
          size="sm"
        />
        <FilterButton
          label="状态"
          value={fState}
          onChange={(v) => {
            setFState(v);
            setPage(1);
          }}
          options={Object.entries(TRI_META).map(([k, m]) => ({
            value: k,
            label: m.label,
            hint: m.desc,
          }))}
          width={260}
        />
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          有效启用 {usable} / 共 {all.length}
        </span>
        <button type="button" className="btn btn-sm btn-accent" onClick={() => setEditor('new')}>
          <Icons.IconPlus size={11} />
          新建标签
        </button>
      </div>

      {/* 只在全部标签都未绑定时展示：「已绑定但停用」同样 usable=0，却可以正常启用，不能套用本说明 */}
      {!isLoading &&
        !isError &&
        all.length > 0 &&
        all.every((t) => t.binding_status === 'unbound') && (
          <div
            className="card"
            style={{
              padding: '10px 14px',
              background: 'var(--warn-soft)',
              borderColor: 'transparent',
            }}
          >
            <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
              <Icons.IconAlert size={13} style={{ color: 'var(--warn)', marginTop: 2 }} />
              <div style={{ fontSize: 11.5, lineHeight: 1.6 }}>
                当前<span style={{ fontWeight: 500 }}>服务端分群规则注册表为空</span>
                ，全部标签处于未绑定态：启停开关锁定、启用一律被服务端拒绝（
                <span className="mono">USER_TAG_RULE_UNBOUND</span>
                ），用户列表的「用户标签」筛选也无候选。
                <span style={{ color: 'var(--fg-muted)' }}>
                  {' '}
                  这不是配置错误：绑定态由 C 端服务端按 tag_key 实时派生，admin
                  不能录入或改写注册表。须依次完成 C 端发版实现对应分群规则、同步更新契约清单、admin
                  镜像该清单三步后，相应标签才在本页转为已绑定、方可启用。
                </span>
              </div>
            </div>
          </div>
        )}

      {isError ? (
        <div className="card" style={{ padding: 24, color: 'var(--neg)' }}>
          标签列表加载失败
        </div>
      ) : isLoading ? (
        <div className="card" style={{ padding: 24, color: 'var(--fg-muted)' }}>
          加载中…
        </div>
      ) : (
        <Section
          title="用户标签清单"
          sub="offset 分页 · 清单是全库标签定义的单一视图，不按 App 分组"
          pad="4px 18px 16px"
        >
          <Table className="tbl">
            <thead>
              <tr>
                <th>tag_key</th>
                <th>名称</th>
                <th>说明</th>
                <th>绑定态</th>
                <th className="num">已打标用户</th>
                <th>启停</th>
                <th>状态</th>
                <th>操作人 · 时间</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((t) => {
                const m = TRI_META[tri(t)];
                const bound = t.binding_status === 'bound';
                const preset = isPresetUserTagKey(t.tag_key);
                return (
                  <tr key={t.id}>
                    <td className="mono" style={{ fontSize: 11.5, fontWeight: 500 }}>
                      {t.tag_key}
                      <span
                        className="chip"
                        style={{ fontSize: 9, marginLeft: 5 }}
                        title={preset ? '§9.3 预置 · tag_key 不可改' : '落行后不可改'}
                      >
                        <Icons.IconLock size={8} />
                      </span>
                      {preset && (
                        <span
                          className="chip"
                          style={{ fontSize: 9, marginLeft: 5 }}
                          title="migration seed 全库一行 · 不可删除、不可改 tag_key"
                        >
                          预置
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      <div
                        title={t.name}
                        style={{
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t.name}
                      </div>
                    </td>
                    <td style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                      <div
                        title={t.description || undefined}
                        style={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t.description || '—'}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`chip ${bound ? 'chip-info' : 'chip-warn'} mono`}
                        style={{ fontSize: 10 }}
                        title={
                          bound
                            ? 'tag_key 命中服务端分群规则注册表（派生值，只读）'
                            : '未命中注册表 · 不可启用'
                        }
                      >
                        {bound ? 'bound' : 'unbound'}
                      </span>
                    </td>
                    <td className="num">—</td>
                    <td>
                      <span
                        title={
                          bound || t.is_active
                            ? undefined
                            : `未绑定不可启用（USER_TAG_RULE_UNBOUND）：tag_key ${t.tag_key} 未命中服务端分群规则注册表。绑定态由 C 端注册表派生，admin 无法录入。`
                        }
                        style={{ display: 'inline-flex' }}
                      >
                        <Switch
                          key={`${t.id}:${switchResetNonce}`}
                          checked={t.is_active}
                          size="sm"
                          locked={!bound && !t.is_active}
                          ariaLabel={`启停标签 ${t.tag_key}`}
                          onChange={() => askToggle(t)}
                        />
                      </span>
                    </td>
                    <td>
                      <span className={`chip ${m.cls}`} style={{ fontSize: 10 }} title={m.desc}>
                        {m.label}
                      </span>
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                      {t.updated_by_name || 'seed'} · {fmtDateTime(t.updated_at)}
                    </td>
                    <td>
                      <ContextMenu
                        items={[
                          {
                            label: '编辑名称 / 说明',
                            icon: 'IconEdit',
                            onClick: () => setEditor(t),
                          },
                          {
                            label: t.is_active ? '停用标签' : '启用标签',
                            icon: t.is_active ? 'IconPause' : 'IconPlay',
                            disabled: !bound && !t.is_active,
                            title:
                              bound || t.is_active
                                ? undefined
                                : '未绑定标签不可启用（USER_TAG_RULE_UNBOUND）',
                            onClick: () => askToggle(t),
                          },
                          '---',
                          {
                            label: '按此标签筛选用户',
                            icon: 'IconUsers',
                            disabled: !(bound && t.is_active),
                            title:
                              bound && t.is_active ? undefined : '只有有效启用的标签可进入人群筛选',
                            onClick: () => onFilterUsers(t.id),
                          },
                          {
                            label: '删除标签',
                            icon: 'IconTrash',
                            disabled: true,
                            title: preset
                              ? '预置标签（migration seed）不可删除'
                              : '无删除端点 · 停止使用请置 is_active=false',
                          },
                        ]}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          style={{ width: 24, height: 24 }}
                          aria-label={`标签操作 ${t.tag_key}`}
                        >
                          <Icons.IconMoreV size={12} />
                        </button>
                      </ContextMenu>
                    </td>
                  </tr>
                );
              })}
              {pageRows.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)' }}
                  >
                    还没有用户标签
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <Pagination total={rows.length} page={page} pageSize={pageSize} onChange={setPage} />
        </Section>
      )}

      <div className="card" style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, marginBottom: 8 }}>三态与能力边界</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {(Object.keys(TRI_META) as Array<keyof typeof TRI_META>).map((k) => (
            <div key={k} className="card-flat" style={{ padding: 10 }}>
              <span className={`chip ${TRI_META[k].cls}`} style={{ fontSize: 10 }}>
                {TRI_META[k].label}
              </span>
              <div
                style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.55 }}
              >
                {TRI_META[k].desc}
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.6 }}>
          新建强制 <span className="mono">is_active=false</span>；编辑仅 name / description（tag_key
          与 is_active / binding_status 不在 PATCH body 内）。
          启停走独立端点：启用有服务端硬校验（未绑定 →{' '}
          <span className="mono">USER_TAG_RULE_UNBOUND</span>），停用恒可执行且保留已有用户关联。
          不提供可视化规则编辑器：规则在服务端代码层持有。
        </div>
      </div>

      {editor !== null && (
        <UserTagEditor tag={editor === 'new' ? null : editor} onClose={() => setEditor(null)} />
      )}
      {toggleOf && (
        <ConfirmDialog
          title={toggleOf.is_active ? `停用「${toggleOf.name}」？` : `启用「${toggleOf.name}」？`}
          message={
            toggleOf.is_active
              ? '停用始终可执行。自动打标停止；已有用户关联（user_tag_assignments）保留不删除，运营消费面按状态过滤后不再命中该标签。'
              : '启用前必须按该标签当前注册表规则完成一次全量重算或增量追平。启用后自动打标恢复，该标签可进入用户列表 / 运营人群筛选。'
          }
          confirmLabel={toggleOf.is_active ? '确认停用' : '确认启用'}
          tone={toggleOf.is_active ? 'warn' : 'default'}
          onClose={() => setToggleOf(null)}
          onConfirm={() => toggle.mutate(toggleOf)}
        />
      )}
    </div>
  );
}
